/**
 * Including this controller in a Trix text field causes the enter key to be
 * remapped to submit the form, and shift+enter must be used to make a new line.
 */
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static outlets = ["agreement-modal"];

  connect() {
    this.editor = this.element;
    this.form = this.element.closest("form");

    this.boundHandleKeyDown = this.handleKeyDown.bind(this);
    this.editor.addEventListener("keydown", this.boundHandleKeyDown);
  }

  disconnect() {
    this.editor.removeEventListener("keydown", this.boundHandleKeyDown);
  }

  isMobile() {
    // Assume any device with an xs display width is mobile.
    // Above sm, we display "press enter to send, shift+enter for new line" text, so
    // users can reasonably expect that behaviour, even on a mobile device. Also, the
    // possibility of mobile devices having physical keyboards makes more nuanced mobile
    // input detection methods both complicated and likely to be inaccurate, so we're
    // keeping it simple and just using width.
    return window.innerWidth < 576;
  }

  handleKeyDown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      if (this.isMobile()) {
        // On mobile devices, enter should act as a new-line input rather than submit the form
        return;
      }

      event.preventDefault();

      if (this.hasAgreementModalOutlet && !this.agreementModalOutlet.requireAgreement(event)) {
        return;
      }

      // if form has multiple submit buttons (such as a form with buttons that appear in
      // different contexts in responsive design), select the first visible one (detected
      // via offsetParent, which is null for display:none elements) and use it for submission,
      // so Rails' UJS state updates are applied to it appropriately
      const submitButtons = this.form.querySelectorAll("input[type='submit']");
      const activeButton =
        Array.from(submitButtons).filter((button) => button.offsetParent != null)[0] ||
        submitButtons[0];

      if (activeButton) {
        this.form.requestSubmit(activeButton);
      } else {
        // fall back to submitting the form directly if no submit button was found
        const submitEvent = new Event("submit", { bubbles: true, cancelable: true });
        this.form.dispatchEvent(submitEvent);
      }
    }
  }
}
